import {
  Box,
  Button,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  DotloopFolderComplianceStatusInput,
  ExternalIngestionDocumentFragment,
  ExternalIngestionFolderFragment,
  ExternalSubmitValidationReviewsInput,
  ExternalValidationReportDataFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentReviewStatus,
  IngestionDocumentValidationErrorReviewFragment,
} from '@client/graphql/__generated__/types';
import { CopyIcon } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { formatPlatformName } from '../utils/platform.utils';
import {
  getContractDocDisplayName,
  getValidationResultsForDocument,
} from '../utils/validation.utils';

interface WriteToPlatformModalProps {
  isOpen: boolean;
  onClose: () => void;
  documents: ExternalIngestionDocumentFragment[];
  documentReviews: IngestionDocumentReviewFragment[];
  validationData: ExternalValidationReportDataFragment | null;
  ingestionFolder: ExternalIngestionFolderFragment;
  dotloopFolderComplianceStatuses?: DotloopFolderComplianceStatusInput[];
  userAddedDocumentIssues?: IngestionDocumentValidationErrorReviewFragment[];
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  onSubmitValidationReviews?: (
    input: Omit<ExternalSubmitValidationReviewsInput, 'ingestionFolderId'>
  ) => void | Promise<void>;
}

export const WriteToPlatformModal: FC<WriteToPlatformModalProps> = ({
  isOpen,
  onClose,
  documents,
  documentReviews,
  validationData,
  ingestionFolder,
  dotloopFolderComplianceStatuses,
  userAddedDocumentIssues = [],
  hiddenDocumentValidationReviews = [],
  onSubmitValidationReviews,
}) => {
  const toast = useToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const [step, setStep] = useState<'update' | 'message'>('update');
  const [capturedMessage, setCapturedMessage] = useState<string>('');

  const documentsWithReviews = useMemo(() => {
    return documents.filter((document) =>
      documentReviews.some(
        (review) => review.ingestionDocument.id === document.id
      )
    );
  }, [documents, documentReviews]);

  // Group user-added document issues by contract document
  const userAddedIssuesByContractDoc = useMemo(() => {
    const groupedIssues = new Map<
      string,
      IngestionDocumentValidationErrorReviewFragment[]
    >();

    userAddedDocumentIssues.forEach((issue) => {
      // Only include issues that have a contractDocumentUuid
      if (issue.contractDocumentUuid) {
        const contractDocId = issue.contractDocumentUuid;
        if (!groupedIssues.has(contractDocId)) {
          groupedIssues.set(contractDocId, []);
        }
        const issues = groupedIssues.get(contractDocId);
        if (issues) {
          issues.push(issue);
        }
      }
    });

    return groupedIssues;
  }, [userAddedDocumentIssues]);

  const summaryMessage = useMemo(() => {
    if (!validationData) {
      return 'No validation data available.';
    }

    // Count total issues across all documents with reviews (validation errors + user-added issues, excluding hidden)
    let totalErrorCount = 0;

    documentsWithReviews.forEach((document) => {
      if (document.contractDocuments && document.contractDocuments.length > 0) {
        document.contractDocuments.forEach((contractDoc) => {
          // Get validation errors and filter out hidden ones
          const { errors } = getValidationResultsForDocument(
            document,
            validationData,
            contractDoc.id
          );

          const visibleValidationErrors = errors.filter((error) => {
            // Check if this validation item is hidden
            return !hiddenDocumentValidationReviews.some(
              (review) =>
                review.ruleUuid === error.ruleUuid &&
                review.contractDocumentUuid === error.contractDocumentUuid &&
                review.isHidden
            );
          });

          // Get user-added issues for this contract document (excluding hidden ones)
          const userAddedIssues =
            userAddedIssuesByContractDoc.get(contractDoc.id) || [];
          const visibleUserAddedIssues = userAddedIssues.filter(
            (issue) => !issue.isHidden
          );

          totalErrorCount +=
            visibleValidationErrors.length + visibleUserAddedIssues.length;
        });
      } else {
        // Handle documents without contract documents
        const { errors } = getValidationResultsForDocument(
          document,
          validationData
        );

        const visibleValidationErrors = errors.filter((error) => {
          return !hiddenDocumentValidationReviews.some(
            (review) =>
              review.ruleUuid === error.ruleUuid &&
              review.contractDocumentUuid === error.contractDocumentUuid &&
              review.isHidden
          );
        });

        totalErrorCount += visibleValidationErrors.length;
      }
    });

    // Handle case with no errors
    let message: string;
    if (totalErrorCount === 0) {
      message = `Good news! All reviewed documents for ${ingestionFolder.folderName} have been processed with no contract issues. Thanks!\n\n`;
    } else {
      message = `Compliance Alert: Please fix these ${totalErrorCount} contract issues for ${ingestionFolder.folderName}. Thanks!\n\n`;
    }

    // Process only documents with reviews and only show errors
    documentsWithReviews.forEach((document) => {
      const documentReview = documentReviews.find(
        (review) => review.ingestionDocument.id === document.id
      );

      let hasErrors = false;
      const documentErrors: string[] = [];

      // Collect all errors for this document
      if (document.contractDocuments && document.contractDocuments.length > 0) {
        document.contractDocuments.forEach((contractDoc) => {
          // Get validation errors and filter out hidden ones
          const { errors } = getValidationResultsForDocument(
            document,
            validationData,
            contractDoc.id
          );

          const visibleValidationErrors = errors.filter((error) => {
            return !hiddenDocumentValidationReviews.some(
              (review) =>
                review.ruleUuid === error.ruleUuid &&
                review.contractDocumentUuid === error.contractDocumentUuid &&
                review.isHidden
            );
          });

          // Get user-added issues for this contract document (excluding hidden ones)
          const userAddedIssues =
            userAddedIssuesByContractDoc.get(contractDoc.id) || [];
          const visibleUserAddedIssues = userAddedIssues.filter(
            (issue) => !issue.isHidden
          );

          if (
            visibleValidationErrors.length > 0 ||
            visibleUserAddedIssues.length > 0
          ) {
            hasErrors = true;
            const contractDocName = getContractDocDisplayName(contractDoc);
            documentErrors.push(`   • ${contractDocName}:`);

            // Add user-added issues first
            visibleUserAddedIssues.forEach((issue) => {
              documentErrors.push(
                `      - ${issue.message || 'User added issue'}`
              );
            });

            // Add validation errors
            visibleValidationErrors.forEach((error) => {
              documentErrors.push(`      - ${error.message}`);
            });
          }
        });
      } else {
        // Handle documents without contract documents
        const { errors } = getValidationResultsForDocument(
          document,
          validationData
        );

        const visibleValidationErrors = errors.filter((error) => {
          return !hiddenDocumentValidationReviews.some(
            (review) =>
              review.ruleUuid === error.ruleUuid &&
              review.contractDocumentUuid === error.contractDocumentUuid &&
              review.isHidden
          );
        });

        if (visibleValidationErrors.length > 0) {
          hasErrors = true;
          documentErrors.push(`   Validation Issues:`);
          visibleValidationErrors.forEach((error) => {
            documentErrors.push(`      - ${error.message}`);
          });
        }
      }

      // Only include document in message if it has errors
      if (hasErrors) {
        message += `${document.documentName}\n`;

        // Add review status if exists
        if (documentReview) {
          const statusText =
            documentReview.status === IngestionDocumentReviewStatus.APPROVED
              ? 'Approved'
              : 'Returned';
          message += `   Status: ${statusText}\n`;
        }

        message += documentErrors.join('\n') + '\n\n';
      }
    });

    return message;
  }, [
    documentReviews,
    validationData,
    ingestionFolder,
    documentsWithReviews,
    userAddedIssuesByContractDoc,
    hiddenDocumentValidationReviews,
  ]);

  const handleCopyMessage = () => {
    void (async () => {
      try {
        await navigator.clipboard.writeText(capturedMessage);
        toast({
          title: 'Message copied',
          description: 'Summary message copied to clipboard',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Copy failed',
          description: 'Could not copy message to clipboard',
          status: 'error',
          duration: 2000,
          isClosable: true,
        });
      }
    })();
  };

  const handleUpdateDocs = () => {
    void (async () => {
      if (!onSubmitValidationReviews) return;

      // Capture the message before updating to prevent it from changing
      setCapturedMessage(summaryMessage);
      setIsUpdating(true);

      try {
        // Call the validation review submission mutation without the message
        // The message will be handled separately in the next step
        await onSubmitValidationReviews({
          dotloopFolderComplianceStatuses,
        });

        toast({
          title: 'Documents updated',
          description: `Updated ${updateCount} documents in ${formatPlatformName(
            ingestionFolder.platform
          )}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Move to message step instead of closing
        setStep('message');
      } catch (error) {
        toast({
          title: 'Update failed',
          description: 'Failed to update documents in platform',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsUpdating(false);
      }
    })();
  };

  const handleClose = () => {
    setStep('update'); // Reset to first step when closing
    setCapturedMessage(''); // Clear captured message
    onClose();
  };

  const platformName = formatPlatformName(ingestionFolder.platform);
  const reviewsForChangedDocs = documentReviews.filter((review) =>
    documentsWithReviews.some((doc) => doc.id === review.ingestionDocument.id)
  );
  const updateCount = reviewsForChangedDocs.length;

  return (
    <Modal isOpen={isOpen} size="lg" onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        {step === 'update' ? (
          <>
            <ModalHeader textAlign="center">
              <Text fontSize="xl" fontWeight="bold">
                Write to {platformName}
              </Text>
            </ModalHeader>

            <ModalBody textAlign="center">
              <Text
                color="compliance.text.secondary"
                fontSize="md"
                fontWeight="normal"
              >
                This will update the status of {updateCount} reviewed document
                {updateCount === 1 ? '' : 's'} in {platformName}. No other
                changes will be made to your documents.
              </Text>
            </ModalBody>

            <ModalFooter>
              <Button
                isDisabled={updateCount === 0}
                isLoading={isUpdating}
                loadingText="Updating..."
                width="100%"
                onClick={handleUpdateDocs}
              >
                Update {updateCount} {updateCount === 1 ? 'doc' : 'docs'} in{' '}
                {platformName}
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader textAlign="center">
              <VStack spacing={2}>
                <Text fontSize="xl" fontWeight="bold">
                  Documents Updated Successfully
                </Text>
                <Text
                  color="compliance.text.secondary"
                  fontSize="md"
                  fontWeight="normal"
                >
                  Your document statuses have been updated in {platformName}.
                  You can optionally copy the message below to notify your agent
                  about the changes.
                </Text>
              </VStack>
            </ModalHeader>

            <ModalBody>
              <VStack spacing={4}>
                <Box position="relative" width="100%">
                  <Textarea
                    readOnly
                    minHeight="200px"
                    placeholder="Summary message will appear here..."
                    resize="vertical"
                    value={capturedMessage}
                  />
                  <IconButton
                    aria-label="Copy message"
                    icon={<CopyIcon size={16} />}
                    pointerEvents="auto"
                    position="absolute"
                    right={2}
                    size="sm"
                    top={2}
                    variant="ghost"
                    zIndex={2}
                    onClick={handleCopyMessage}
                  />
                </Box>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" onClick={handleClose}>
                Done
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
